import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Check, Sparkles, Users, Zap, ArrowLeft, Loader2, LogIn } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { useAuth } from "./AuthContext";
import { createCheckout, startGoogleLogin } from "@/lib/rexxponsApi";

const TIERS = [
  {
    id: "free",
    name: "Free",
    price: "€0",
    cadence: "forever",
    icon: Zap,
    lookup: null,
    features: [
      "NexxSearch structured insight",
      "Cognitive Reader for any URL",
      "Read · Interpret · Explore modes",
      "Follow-up chat on reports",
      "Quick & Balanced reasoning",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "€19",
    cadence: "/month",
    icon: Sparkles,
    lookup: "rexxpons_pro_monthly",
    highlight: true,
    features: [
      "Everything in Free",
      "Multi-Source Cross-Referencing",
      "Document upload & deep research",
      "Deep & Critical reasoning depth",
      "One-click translation (13 languages)",
      "Priority processing",
    ],
  },
  {
    id: "team",
    name: "Team",
    price: "€49",
    cadence: "/month",
    icon: Users,
    lookup: "rexxpons_team_monthly",
    features: [
      "Everything in Pro",
      "Shared team workspace",
      "Centralised billing",
      "Higher rate limits",
      "Priority support",
    ],
  },
];

export default function Pricing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const currentPlan = user?.plan || "free";

  const onChoose = async (tier) => {
    if (!tier.lookup) {
      navigate("/");
      return;
    }
    if (!user) {
      toast.info("Please sign in to upgrade.");
      startGoogleLogin();
      return;
    }
    setBusy(tier.id);
    try {
      const { checkout_url } = await createCheckout(tier.lookup);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setBusy(null);
    }
  };

  return (
    <div className="nexx-ambient min-h-screen bg-background text-foreground">
      <Toaster position="top-center" theme="dark" />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <button
          data-testid="pricing-back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground mb-10 transition-colors"
        >
          <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Back
        </button>

        <div className="text-center mb-4">
          <Wordmark className="text-2xl text-foreground" />
        </div>
        <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight text-center mb-3">Upgrade your understanding</h1>
        <p className="text-muted-foreground text-center max-w-xl mx-auto mb-14">
          Start free. Unlock cross-referencing, document deep-research and more when you need forensic-grade rigor.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-tiers">
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            const isCurrent = currentPlan === tier.id;
            return (
              <div
                key={tier.id}
                data-testid={`tier-${tier.id}`}
                className={`relative rounded-2xl border p-7 flex flex-col ${
                  tier.highlight ? "border-primary/60 bg-primary/[0.04] nexx-glow" : "border-border bg-card"
                }`}
              >
                {tier.highlight && (
                  <span className="absolute -top-3 left-7 text-[10px] tracking-[0.18em] uppercase bg-primary text-primary-foreground rounded-full px-3 py-1">
                    Most popular
                  </span>
                )}
                <div className="flex items-center gap-2.5 mb-4">
                  <Icon strokeWidth={1.5} className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl">{tier.name}</h2>
                </div>
                <div className="mb-6">
                  <span className="font-heading text-4xl font-light">{tier.price}</span>
                  <span className="text-muted-foreground text-sm ml-1">{tier.cadence}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/85">
                      <Check strokeWidth={2} className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`tier-${tier.id}-btn`}
                  onClick={() => onChoose(tier)}
                  disabled={isCurrent || busy === tier.id || loading}
                  className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium tracking-[0.06em] uppercase transition-opacity disabled:opacity-50 ${
                    tier.highlight ? "bg-primary text-primary-foreground hover:opacity-90" : "border border-border text-foreground hover:bg-secondary"
                  }`}
                >
                  {busy === tier.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  {!user && tier.lookup && <LogIn strokeWidth={1.7} className="w-4 h-4" />}
                  {isCurrent ? "Current plan" : tier.lookup ? (user ? "Upgrade" : "Sign in to upgrade") : "Get started"}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted-foreground/70 mt-10">
          Secure payment via Stripe. Cancel anytime. Prices in EUR (€), tax handled at checkout.
        </p>
      </div>
    </div>
  );
}
